/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */


import * as $ from "jquery";
import {UiComboBox} from "./formfield/UiComboBox";

import {UiTree_NodeSelectedEvent, UiTree_RequestTreeDataEvent, UiTreeCommandHandler, UiTreeConfig, UiTreeEventSource} from "../generated/UiTreeConfig";
import {TeamAppsEvent} from "./util/TeamAppsEvent";
import {defaultTreeQueryFunctionFactory, ResultCallback, trivialMatch, TrivialTree} from "trivial-components";
import {UiComponent} from "./UiComponent";
import {buildObjectTree, buildTreeEntryHierarchy, matchingModesMapping, Renderer} from "./Common";
import {TeamAppsUiContext} from "./TeamAppsUiContext";
import {EventFactory} from "../generated/EventFactory";
import {TeamAppsUiComponentRegistry} from "./TeamAppsUiComponentRegistry";

/**
 * @author Yann Massard (yamass@gmail.com)
 */

export class UiTree extends UiComponent<UiTreeConfig> implements UiTreeCommandHandler, UiTreeEventSource {

	public readonly onNodeSelected: TeamAppsEvent<UiTree_NodeSelectedEvent> = new TeamAppsEvent<UiTree_NodeSelectedEvent>(this);
	public readonly onRequestTreeData: TeamAppsEvent<UiTree_RequestTreeDataEvent> = new TeamAppsEvent<UiTree_RequestTreeDataEvent>(this);

	private $panel: JQuery;
	private trivialTree: TrivialTree<any>;
	private nodes: any[];
	private templates: { [name: string]: Renderer };

	constructor(config: UiTreeConfig, context: TeamAppsUiContext) {
		super(config, context);
		this.$panel = $('<div class="UiTree" autocomplete="off">');
		const $input = $('<input>').appendTo(this.$panel);

		this.templates = context.templateRegistry.createTemplateRenderers(config.templates, config.idPropertyName);

		this.nodes = buildObjectTree(config.treeData, config.idPropertyName, config.parentIdPropertyName);

		let trivialMatchingOptions = UiComboBox.createTrivialMatchingOptions(config);
		let customQueryFunction = defaultTreeQueryFunctionFactory(() => this.nodes, (entry, queryString, nodeDepth) => {
			if (config.matchFieldNames) {
				return config.matchFieldNames.some((fieldName: keyof typeof entry) => entry[fieldName] && trivialMatch(entry[fieldName].toString(), queryString, trivialMatchingOptions).length > 0);
			} else {
				return Object.keys(entry).some((fieldName: keyof typeof entry) => entry[fieldName] && trivialMatch(entry[fieldName].toString(), queryString, trivialMatchingOptions).length > 0);
			}
		}, "__children", config.expandedProperty);
		let queryFunction = (queryString: string, resultCallback: ResultCallback<any>) => {
			customQueryFunction(queryString, resultCallback);
		};

		this.trivialTree = new TrivialTree<any>($input, {
			entries: this.nodes,
			childrenProperty: "__children",
			expandedProperty: config.expandedProperty,
			entryRenderingFunction: (entry, depth) => {
				let entryTemplateName = entry[config.templateProperty];
				let entryRenderer = entryTemplateName && (this.templates[entryTemplateName] || context.templateRegistry.getTemplateRendererByName(entryTemplateName));
				let defaultTemplateName = config.defaultTreeTemplateNames[Math.min(config.defaultTreeTemplateNames.length - 1, depth)];
				let defaultRenderer = this.templates[defaultTemplateName] || context.templateRegistry.getTemplateRendererByName(defaultTemplateName);
				let renderer = entryRenderer || defaultRenderer;
				return renderer.render(entry);
			},
			searchBarMode: 'show-if-filled',
			lazyChildrenFlag: config.lazyChildrenFlagProperty,
			lazyChildrenQueryFunction: (node, resultCallback) => {
				this.onRequestTreeData.fire(EventFactory.createUiTree_RequestTreeDataEvent(config.id, node && node[config.idPropertyName]))
			},
			spinnerTemplate: `<div class="UiSpinner" style="height: 20px; width: 20px; margin: 4px auto 4px auto;"></div>`,
			queryFunction: queryFunction,
			showExpanders: config.showExpanders,
			expandOnSelection: config.openOnSelection,
			enforceSingleExpandedPath: config.enforceSingleExpandedPath,
			inputValueFunction: entry => entry && entry[config.idPropertyName],
			idFunction: entry => entry && entry[config.idPropertyName],
			indentation: config.indentation,
			animationDuration: config.animate ? 70 : 0,
			matchingOptions: {
				matchingMode: matchingModesMapping[config.textMatchingMode]
			}
		});
		this.trivialTree.onSelectedEntryChanged.addListener((entry) => {
			this.onNodeSelected.fire(EventFactory.createUiTree_NodeSelectedEvent(config.id, entry));
		});
	}

	public getMainDomElement(): JQuery {
		return this.$panel;
	}

	setData(treeData: any[]): void {
		this.nodes = buildTreeEntryHierarchy(treeData, this._config.idPropertyName, this._config.parentIdPropertyName);
		this.trivialTree.updateEntries(this.nodes);
	}

	setChildren(parentId: any, children: any[]): void {
		this.trivialTree.updateChildren(parentId, buildTreeEntryHierarchy(children, this._config.idPropertyName, this._config.parentIdPropertyName));
	}

	public addOrUpdateNode(nodeConfig: any) {
		const node = buildObjectTree([nodeConfig], this._config.idPropertyName, this._config.parentIdPropertyName)[0];
		const existingNode = this.findEntryById(node[this._config.idPropertyName]);
		if (existingNode) {
			// update node's properties, but not its children!
			for (let property in node) {
				if (node.hasOwnProperty(property) && property != "children") {
					existingNode[property] = node[property];
				}
			}
		} else {
			const parentNode = this.findEntryById(node[this._config.parentIdPropertyName]);
			if (parentNode != null) {
				if (!parentNode.__children) {
					parentNode.__children = [];
				}
				parentNode.__children.push(node);
			} else {
				this.nodes.push(node);
			}
		}
		this.trivialTree.updateEntries(this.nodes);
	}

	public relocateNode(recordId: any, newParentRecordId: any): void {
		const childNode = this.findEntryById(recordId);
		const newParentNode = this.findEntryById(newParentRecordId);
		if (childNode && newParentNode) {
			const oldParentNode = this.findParentNode(childNode[this._config.idPropertyName]);
			const childrenArray: any[] = oldParentNode ? oldParentNode.__children : this.nodes;
			childrenArray.splice(childrenArray.indexOf(childNode), 1);
			newParentNode.__children.push(childNode);
		}
		this.trivialTree.updateEntries(this.nodes);
	}

	public removeNode(recordId: any): void {
		const childNode = this.findEntryById(recordId);
		if (childNode) {
			const oldParentNode = this.findParentNode(childNode[this._config.idPropertyName]);
			const childrenArray: any[] = oldParentNode ? oldParentNode.__children : this.nodes;
			childrenArray.splice(childrenArray.indexOf(childNode), 1);
		}
		this.trivialTree.updateEntries(this.nodes);
	}

	public removeAllNodes() {
		this.nodes = [];
		this.trivialTree.updateEntries(this.nodes);
	}

	public selectNode(recordId: any): void {
		this.trivialTree.selectNodeById(recordId);
	}

	private findEntries(filterFunction: (Entry: any) => boolean) {
		function findEntriesInSubTree(node: any, listOfFoundEntries: any[]) {
			if (filterFunction.call(this, node)) {
				listOfFoundEntries.push(node);
			}
			if (node.__children) {
				for (let i = 0; i < node.__children.length; i++) {
					const child = node.__children[i];
					findEntriesInSubTree(child, listOfFoundEntries);
				}
			}
		}

		const matchingEntries: any[] = [];
		for (let i = 0; i < this.nodes.length; i++) {
			const rootEntry = this.nodes[i];
			findEntriesInSubTree(rootEntry, matchingEntries);
		}
		return matchingEntries;
	}

	private findEntryById(id: string | number) {
		return this.findEntries((entry: any) => {
			return entry[this._config.idPropertyName] === id;
		})[0];
	}

	private findParentNode(childId: string | number) {
		return this.findEntries((entry) => {
			if (entry.__children) {
				for (let i = 0; i < entry.__children.length; i++) {
					if (entry.__children[i][this._config.idPropertyName] === childId) {
						return true;
					}
				}
			}
			return false;
		})[0];
	}

	public destroy(): void {
		// nothing to do
	}
}

TeamAppsUiComponentRegistry.registerComponentClass("UiTree", UiTree);